import type { ReportColumnState } from '../data-layer/violationsReportTypes';

export type ReportType = 'SUMMARY' | 'DETAIL' | 'PERMISSIONS';

export interface ColumnConfig {
  columnOrder: Record<string, number>;
  selectedColumns: string[];
}

/**
 * Build columnDisplay object from selectedColumns and Redux data
 * @param selectedColumns - Array of column IDs to include in columnDisplay
 * @param columnsData - Redux data containing columns grouped by category
 * @param localize - Localization function to translate localizationKey to display text
 * @returns Object mapping column IDs to their localized display values
 */
export const buildColumnDisplay = (
  selectedColumns: string[],
  columnsData: Record<string, ReportColumnState[]> | undefined,
  localize: (
    key: string | { id: string; defaultMessage: string },
    placeholders?: object
  ) => string
): Record<string, string> => {
  const columnDisplay: Record<string, string> = {};

  // If no columns data, fallback to column IDs as values
  if (!columnsData) {
    selectedColumns.forEach((columnId) => {
      columnDisplay[columnId] = columnId;
    });

    return columnDisplay;
  }

  // Flatten all groups into a single array for searching
  const allColumns = Object.values(columnsData).flat();

  // Build a map of column id -> localizationKey for quick lookup
  const columnMap = new Map<string, string>();

  allColumns.forEach((column) => {
    if (column.id && column.localizationKey) {
      columnMap.set(column.id, column.localizationKey);
    }
  });

  // Build columnDisplay: use localized value if found, otherwise use columnId
  selectedColumns.forEach((columnId) => {
    const localizationKey = columnMap.get(columnId);

    if (localizationKey) {
      columnDisplay[columnId] = localize(localizationKey);
    } else {
      columnDisplay[columnId] = columnId;
    }
  });

  return columnDisplay;
};

/**
 * Builds column config from API report-columns response (default columns only, API order).
 */
export const buildColumnConfigFromApiColumns = (
  columnsData: Record<string, ReportColumnState[]>
): ColumnConfig => {
  const selectedColumns = Object.values(columnsData).flatMap((group) =>
    group
      .filter((column) => column.isDefault)
      .map((column) => column.id)
      .filter((id): id is string => Boolean(id))
  );

  const columnOrder = Object.fromEntries(
    selectedColumns.map((id, index) => [id, index + 1])
  );

  return { selectedColumns, columnOrder };
};

/**
 * Get column configuration based on report type
 */
export const getColumnConfig = (reportType: ReportType): ColumnConfig => {
  const baseColumns = {
    riskName: 1,
    riskDescription: 2,
    riskPriority: 3,
    riskType: 4,
    riskCustomProperty1: 5,
    riskCustomProperty2: 6,
    riskOwner: 7,
    riskStatus: 8,
    functionName: 9,
    functionType: 10,
    functionOwner: 11,
    userName: 12,
    userFirstName: 13,
    userLastName: 14,
    city: 15,
    companyName: 16,
    costCenter: 17,
    country: 18,
    departmentName: 19,
    userDisplayName: 20,
    email: 21,
    employeeClass: 22,
    employeeId: 23,
  };

  if (reportType === 'SUMMARY') {
    return {
      columnOrder: {
        ...baseColumns,
        assignedEntitlementDisplayNames: 24,
        assignedEntitlementCustomProperty4s: 25,
        assignedEntitlementCustomProperty5s: 26,
        assignedEntitlementDescriptions: 27,
        assignedEntitlementGlossaries: 28,
        endpointDisplayNames: 29,
        violationStatus: 30,
        actualVsPotential: 31,
      },
      selectedColumns: [
        ...Object.keys(baseColumns),
        'assignedEntitlementDisplayNames',
        'assignedEntitlementCustomProperty4s',
        'assignedEntitlementCustomProperty5s',
        'assignedEntitlementDescriptions',
        'assignedEntitlementGlossaries',
        'endpointDisplayNames',
        'violationStatus',
        'actualVsPotential',
      ],
    };
  }

  if (reportType === 'DETAIL') {
    return {
      columnOrder: {
        ...baseColumns,
        assignedEntitlementDisplayName: 24,
        violatingEntitlementDisplayNames: 25,
        assignedEntitlementCustomProperty4: 26,
        assignedEntitlementCustomProperty5: 27,
        assignedEntitlementDescription: 28,
        assignedEntitlementGlossary: 29,
        endpointDisplayName: 30,
        violationStatus: 31,
        actualVsPotential: 32,
      },
      selectedColumns: [
        ...Object.keys(baseColumns),
        'assignedEntitlementDisplayName',
        'violatingEntitlementDisplayNames',
        'assignedEntitlementCustomProperty4',
        'assignedEntitlementCustomProperty5',
        'assignedEntitlementDescription',
        'assignedEntitlementGlossary',
        'endpointDisplayName',
        'violationStatus',
        'actualVsPotential',
      ],
    };
  }

  // PERMISSIONS
  return {
    columnOrder: {
      ...baseColumns,
      assignedEntitlementDisplayName: 24,
      violatingEntitlementDisplayName: 25,
      functionEntitlementDisplayName: 26,
      assignedEntitlementCustomProperty4: 27,
      assignedEntitlementCustomProperty5: 28,
      assignedEntitlementDescription: 29,
      assignedEntitlementGlossary: 30,
      endpointDisplayName: 31,
      violationStatus: 32,
      actualVsPotential: 33,
    },
    selectedColumns: [
      ...Object.keys(baseColumns),
      'assignedEntitlementDisplayName',
      'violatingEntitlementDisplayName',
      'functionEntitlementDisplayName',
      'assignedEntitlementCustomProperty4',
      'assignedEntitlementCustomProperty5',
      'assignedEntitlementDescription',
      'assignedEntitlementGlossary',
      'endpointDisplayName',
      'violationStatus',
      'actualVsPotential',
    ],
  };
};
